/**
 * 69.17 step 3: a dropship flown by a person (DECISIONS 236: *"any ship"*, *"autopilot back to previous routine"*).
 *
 * - **Taking it.** A person standing within PILOTED_FLIGHT.reachPeople of a ship's foot — a wave ship parked on its pad (any
 *   side's), or a flight standing landed — takes its pilot's seat (`pilot: 'take'`). The flight's body (core/flightModel.ts)
 *   starts where the ship stands; its schedule's ticks are put out of reach (nothing of the path fires while it is flown); the
 *   person is aboard (`boarded`, `piloting`). A pad's ship becomes a flight from its pad (`fromPad`, `padOf` when another side
 *   took it), so its pad stands empty and its side's sorties wait for it as for any sortie; the riders aboard on the pad stay
 *   aboard when its own side takes it, and step off at the ramp when another does. A landed flight's riders still waiting to
 *   come in (the fallen, its own side's) leave it for the next ship when another side takes it. 69.74 step 2c: a helicopter
 *   parked on its pad's spot (any side's) too — a helicopter's flight from its spot (`padSpot`; `padOf` when another side
 *   took it), its spot out; handed back with nobody aboard it flies home to that spot (and turns round there).
 * - **Flying it.** Each tick the pilot's controls (`fly`) step the body, the ground under it (the fine ground, a pad's deck)
 *   stopping it: a touchdown past PILOTED_FLIGHT.hard wears the hull by its speed, past `crash` loses the ship (its pilot and
 *   riders ride the crash down). On the ground after the ramp's time, the riders aboard walk out, one by one, as at a landing.
 * - **Leaving it.** On the ground the pilot steps out beside the cockpit; in the air the pilot stays aboard as a rider. Either
 *   way the ship goes back to its routine on a fresh path (core/dropship.ts rejoinFlight, the bridge from where it was): its
 *   riders aboard and its first landing not made — that landing; riders aboard and landed once — the nearest clear landing
 *   under it, then out by its exit; nobody aboard — home (a wave ship to its own pad, its side again; a called ship off the
 *   board). A pilot dead or gone hands it back the same way.
 */
import type { DropshipFlight, GameState, PlayerState, UnitIntent, Vec3 } from '../types';
import { DROPSHIP, PHYS_DT } from '../constants';
import { DROPSHIP_FLIGHT, PILOTED_FLIGHT } from '../constants/flightModel';
import { newCraft, stepCraft, type FlightEnv } from './flightModel';
import { flightNow } from './shipNow';
import { parkedFrame, parkedHeliFrame, SHIP_PARTS } from './shipBody';
import { padOut, parkedHelis } from './helipad';   // 69.74 step 2c
import { parkedShips, padRampFoot, boardingSortie, rejoinFlight, clearLanding } from './dropship';
import { shipBlow, loseShip, shipsOn } from './shipDamage';
import { personSurfaceRadius } from './fineTerrain';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { rightOf } from './surfaceWalk';
import { dacos } from './dmath';
import { vecAdd, vecScale, vecSub, vecDot, vecNormalize } from '../math';

/** The schedule's ticks while a person flies it: never reached. */
const NEVER = Number.MAX_SAFE_INTEGER;
const personOf = (state: Pick<GameState, 'config'>) => PERSON_BODY_HEIGHT * personSetOf(state).k;

/** What a piloted ship did this tick, for the host. */
export type PilotEvent =
  | { kind: 'shipTaken'; unitId: number; id: number }
  | { kind: 'shipLeft'; unitId: number; id: number; aboard: boolean }
  | { kind: 'shipTouchdown'; id: number; speed: number };

/** The world a ship flies over on its planet: the planet's centre, the fine ground under it, a pad's deck where one stands. */
function envOf(state: GameState, planetIndex: number): FlightEnv {
  const planet = state.planets[planetIndex], ps = personSetOf(state), person = personOf(state);
  const pads = Object.values(state.shipPads ?? {}).filter(p => p.planetIndex === planetIndex && (p.deck ?? 0) > 0);
  return {
    centre: planet.position,
    ground: dir => {
      let deck = 0;
      for (const p of pads) if (dacos(Math.min(1, vecDot(dir, vecNormalize(p.dir)))) * planet.radius < 4.5 * person) deck = Math.max(deck, p.deck ?? 0);
      return personSurfaceRadius(planet, dir, ps) + deck;
    },
  };
}

/** The surface distance between two directions on planet `planetIndex` (world). */
const arc = (state: GameState, planetIndex: number, a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * state.planets[planetIndex].radius;

/** The ship nearest `u` it can take the seat of: a parked wave ship (its pad's side), a flight standing landed (its id), or (69.74 step 2c) a helicopter parked on its spot. */
export function shipInReach(state: GameState, u: PlayerState, tick: number): { pad: number } | { flight: number } | { parked: { team: number; spot: number } } | null {
  if (!u.alive || u.boarded || u.mount || u.airborne || u.planetIndex < 0) return null;
  const reach = PILOTED_FLIGHT.reachPeople * personOf(state);
  let best: { pad: number } | { flight: number } | { parked: { team: number; spot: number } } | null = null, bestD = reach;
  for (const p of parkedShips(state)) {
    if (p.planetIndex !== u.planetIndex) continue;
    const d = arc(state, u.planetIndex, p.dir, u.surfaceDir);
    if (d <= bestD) { best = { pad: p.team }; bestD = d; }
  }
  for (const h of state.dropships?.pads ? parkedHelis(state) : []) {   // 69.74 step 2c
    if (h.planetIndex !== u.planetIndex) continue;
    const d = arc(state, u.planetIndex, h.dir, u.surfaceDir);
    if (d <= bestD) { best = { parked: { team: h.team, spot: h.spot } }; bestD = d; }
  }
  for (const f of state.dropships?.flights ?? []) {
    if (f.piloted || f.planetIndex !== u.planetIndex) continue;
    const now = flightNow(state, f, tick);
    if (!now || now.flight!.pose.phase !== 'down') continue;
    const d = arc(state, u.planetIndex, now.flight!.pose.dir, u.surfaceDir);
    if (d <= bestD) { best = { flight: f.id }; bestD = d; }
  }
  return best;
}

/** `u` takes the pilot's seat of the ship in its reach at `tick` (unchanged when there is none). */
export function takeShip(state: GameState, unitId: number, tick: number): { state: GameState; event?: PilotEvent } {
  const u = state.players.find(p => p.id === unitId);
  const target = u ? shipInReach(state, u, tick) : null;
  if (!u || !target) return { state };
  const person = personOf(state), team = u.teamIndex;
  const ds = state.dropships ?? { left: {}, issued: {}, flights: [], next: 0 };
  let players = state.players, sorties = state.sorties, flights = ds.flights, id: number, next = ds.next, taken: typeof ds | null = null;
  if ('pad' in target) {
    const owner = target.pad, pad = state.shipPads![owner], fr = parkedFrame(state, owner)!;
    const env = envOf(state, pad.planetIndex);
    // its riders aboard on the pad: they fly with their own side's pilot; another side's pilot puts them off at the ramp (their sortie waits for the ship)
    const q = boardingSortie(state, owner), aboard = q ? q.units.filter(x => players.find(p => p.id === x)?.boarded) : [];
    const own = owner === team, foot = padRampFoot(state, owner);
    if (own && q) sorties = (sorties ?? []).flatMap(x => (x !== q ? [x] : x.units.filter(v => !aboard.includes(v)).length ? [{ ...x, units: x.units.filter(v => !aboard.includes(v)) }] : []));
    if (!own) players = players.map(p => (aboard.includes(p.id) ? { ...p, boarded: undefined, planetIndex: foot.planetIndex, surfaceDir: foot.surfaceDir } : p));
    id = next++;
    const body = newCraft('dropship', vecAdd(fr.origin, vecScale(fr.up, DROPSHIP_FLIGHT.gear * person)), fr.front, { x: 0, y: 0, z: 0 }, env, true);
    const flight: DropshipFlight = {
      id, team, payload: 'troops', planetIndex: pad.planetIndex, origin: pad.dir, land: pad.dir, facing: pad.facing, called: pad.dir,
      calledTick: tick, landTick: NEVER, exitTick: NEVER, liftTick: NEVER, goneTick: NEVER, riders: own ? aboard : [], out: 0, announced: true, fromPad: true,
      ...(own ? {} : { padOf: owner }), ...(pad.health !== undefined ? { health: pad.health } : {}), ...(pad.parts ? { parts: [...pad.parts] } : {}),
      piloted: { body, pilot: unitId, tick, route: pad.dir, delivered: true, landedAt: tick },
    };
    flights = [...flights, flight];
  } else if ('parked' in target) {
    // 69.74 step 2c: the helicopter on its spot — a helicopter's flight from the spot, home to it; the spot out, one fewer ready
    const { team: owner, spot } = target.parked, h = parkedHelis(state).find(x => x.team === owner && x.spot === spot)!, fr = parkedHeliFrame(state, h);
    id = next++;
    const body = newCraft('dropship', vecAdd(fr.origin, vecScale(fr.up, DROPSHIP_FLIGHT.gear * person)), fr.front, { x: 0, y: 0, z: 0 }, envOf(state, h.planetIndex), true);
    const flight: DropshipFlight = {
      id, team, payload: 'troops', craft: 'heli', planetIndex: h.planetIndex, origin: h.dir, land: h.dir, facing: h.facing, called: h.dir,
      calledTick: tick, landTick: NEVER, exitTick: NEVER, liftTick: NEVER, goneTick: NEVER, riders: [], out: 0, announced: true, padSpot: spot,
      ...(owner === team ? {} : { padOf: owner }), ...(h.health !== undefined ? { health: h.health } : {}), ...(h.parts ? { parts: [...h.parts] } : {}),
      piloted: { body, pilot: unitId, tick, route: h.dir, delivered: true, landedAt: tick },
    };
    flights = [...flights, flight];
    taken = padOut({ ...ds, flights }, owner, spot, id);
    const pad = taken.pads![owner], stands = state.structures.some(x => x.id === pad.structureId && x.health > 0);
    taken = { ...taken, helis: { ...taken.helis, [owner]: stands ? pad.spots.filter(x => x.state === 'ready').length : 0 } };
  } else {
    const f = flights.find(x => x.id === target.flight)!, now = flightNow(state, f, tick)!, fr = now.frame;
    const body = newCraft('dropship', vecAdd(fr.origin, vecScale(fr.up, DROPSHIP_FLIGHT.gear * person)), fr.front, { x: 0, y: 0, z: 0 }, envOf(state, f.planetIndex), true);
    const own = f.team === team;
    const { rejoin: _r, ...rest } = f;
    const taken: DropshipFlight = {
      ...rest, team, landTick: NEVER, exitTick: NEVER, liftTick: NEVER, goneTick: NEVER,
      ...(own ? {} : { riders: f.riders.slice(0, f.out) }),   // (another side's fallen waiting to come in wait for their next ship)
      ...(f.fromPad && !own ? { padOf: f.padOf ?? f.team } : {}),
      piloted: { body, pilot: unitId, tick, route: f.land, delivered: tick >= f.landTick, landedAt: tick },
    };
    flights = flights.map(x => (x.id === f.id ? taken : x));
    id = f.id;
  }
  players = players.map(p => (p.id === unitId ? { ...p, boarded: true as const, piloting: id, stance: undefined } : p));
  return { state: { ...state, players, ...(sorties !== state.sorties ? { sorties } : {}), dropships: { ...(taken ?? ds), flights, next } }, event: { kind: 'shipTaken', unitId, id } };
}

/**
 * Flight `f` handed back at `tick` (its pilot `left`, alive, when the pilot chose to leave): on the ground the pilot steps
 * out beside the cockpit, in the air stays aboard as a rider; the ship on a fresh path to its routine (see the header).
 */
export function handBack(state: GameState, f: DropshipFlight, tick: number, left: number | null): { state: GameState; event?: PilotEvent } {
  const p = f.piloted!, planet = state.planets[f.planetIndex], person = personOf(state);
  const now = flightNow(state, f, tick)!, pose = now.flight!.pose, ground = !!p.body.landed;
  let players = state.players, riders = f.riders;
  if (left !== null) {
    if (ground) {
      const side = vecScale(rightOf(now.frame.front, vecNormalize(pose.dir)), -1);
      const spot = vecNormalize(vecAdd(vecNormalize(pose.dir), vecScale(side, (2.6 * person) / planet.radius)));
      players = players.map(q => (q.id === left ? { ...q, boarded: undefined, piloting: undefined, planetIndex: f.planetIndex, surfaceDir: spot, heading: now.frame.front } : q));
    } else {
      riders = [...riders, left];
      players = players.map(q => (q.id === left ? { ...q, piloting: undefined } : q));
    }
  }
  const aboard = riders.length > f.out, home = f.padOf ?? f.team;
  const based: DropshipFlight = { ...f, riders, ...(f.padOf !== undefined && !aboard ? { team: home, padOf: undefined } : {}) };
  const { padOf: _po, ...plain } = based;
  const g: DropshipFlight = based.padOf === undefined ? plain : based;
  let land: Vec3, end = false;
  if (aboard && !p.delivered) land = p.route;
  else if (aboard) land = clearLanding(state, f.planetIndex, vecNormalize(pose.dir)) ?? vecNormalize(pose.dir);
  else { land = f.fromPad ? state.shipPads?.[home]?.dir ?? f.origin : f.origin; end = true; }
  const from = { pos: pose.pos, vel: ground ? { x: 0, y: 0, z: 0 } : p.body.vel };
  const flight = rejoinFlight(state, g, tick, from, land, end);
  const flights = state.dropships!.flights.map(x => (x.id === f.id ? flight : x));
  return { state: { ...state, players, dropships: { ...state.dropships!, flights } }, ...(left !== null ? { event: { kind: 'shipLeft', unitId: left, id: f.id, aboard: !ground } } : {}) };
}

/**
 * A tick of the piloted ships (`intents` the players', aligned): each pilot's controls step its ship; a touchdown past the
 * hard landing's speed wears it, past the crash's loses it; on the ground its riders walk out after the ramp; a pilot who
 * leaves (or is gone) hands it back. Nothing when no ship is flown.
 */
export function stepPiloted(state: GameState, intents: readonly (UnitIntent | undefined)[], tick: number): { state: GameState; events: PilotEvent[] } {
  const events: PilotEvent[] = [];
  const flown = (state.dropships?.flights ?? []).filter(f => f.piloted).map(f => f.id);
  if (!flown.length) {
    // (a pilot whose ship is gone — lost, its crash carrying the pilot — is no longer at the controls)
    if (state.players.some(p => p.piloting !== undefined)) state = { ...state, players: state.players.map(p => (p.piloting === undefined ? p : { ...p, piloting: undefined })) };
    return { state, events };
  }
  const person = personOf(state), step = Math.round(DROPSHIP.exitSeconds / PHYS_DT), ramp = Math.round(DROPSHIP.rampSeconds / PHYS_DT);
  for (const id of flown) {
    const f = state.dropships?.flights.find(x => x.id === id);
    if (!f?.piloted) continue;
    const p = f.piloted, i = state.players.findIndex(q => q.id === p.pilot), pilot = state.players[i];
    if (!pilot || !pilot.alive || pilot.piloting !== f.id) { state = handBack(state, f, tick, null).state; continue; }
    const intent = intents[i];
    if (intent?.pilot === 'leave') { const r = handBack(state, f, tick, pilot.id); state = r.state; if (r.event) events.push(r.event); continue; }
    const lost = f.craft === 'heli' ? [] : SHIP_PARTS.slice(0, DROPSHIP_FLIGHT.nacelles.length).map((_, k) => (f.parts?.[k] ?? 1) <= 0);   // (69.74 step 2c: a helicopter's parts are not nacelles: a lost engine or rotor loses it)
    const r = stepCraft(p.body, intent?.fly ?? {}, envOf(state, f.planetIndex), PHYS_DT, person, lost);
    const body = r.body;
    let g: DropshipFlight = { ...f, piloted: { ...p, body, tick: tick + 1 } };
    if (body.landed && !p.body.landed) {
      // down: where it stands is its landing (the riders' walk out reads it); its first landing made
      const dir = vecNormalize(vecSub(body.pos, state.planets[f.planetIndex].position));
      g = { ...g, land: dir, facing: body.front, piloted: { ...g.piloted!, landedAt: tick, delivered: true } };
    }
    g = { ...g, exitTick: body.landed && g.piloted!.landedAt !== undefined ? g.piloted!.landedAt + ramp - g.out * step : NEVER };
    state = { ...state, dropships: { ...state.dropships!, flights: state.dropships!.flights.map(x => (x.id === f.id ? g : x)) } };
    // the pilot goes where the ship goes (aboard: out of the world, but its place the ship's)
    const at = vecNormalize(vecSub(body.pos, state.planets[f.planetIndex].position));
    state = { ...state, players: state.players.map(q => (q.id === pilot.id ? { ...q, surfaceDir: at, planetIndex: f.planetIndex } : q)) };
    if (r.touch && r.touch.speed > 0.5) {
      events.push({ kind: 'shipTouchdown', id: f.id, speed: r.touch.speed });
      if (r.touch.speed > PILOTED_FLIGHT.hard && shipsOn(state)) {
        state = r.touch.speed > PILOTED_FLIGHT.crash
          ? loseShip(state, f.id, f.planetIndex, tick, 'hull')
          : shipBlow(state, f.id, f.planetIndex, body.pos, (r.touch.speed - PILOTED_FLIGHT.hard) * PILOTED_FLIGHT.hardDamage, 'blast');
      }
    }
  }
  // (a pilot whose ship was lost this tick or before is no longer at the controls)
  const still = new Set((state.dropships?.flights ?? []).filter(f => f.piloted).map(f => f.piloted!.pilot));
  if (state.players.some(q => q.piloting !== undefined && !still.has(q.id))) state = { ...state, players: state.players.map(q => (q.piloting !== undefined && !still.has(q.id) ? { ...q, piloting: undefined } : q)) };
  return { state, events };
}
